import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isPublicSafeUsername } from '../lib/wingo-username';

const ChallengeEntry: React.FC = () => {
  const [usernames, setUsernames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState<string>('');
  const navigate = useNavigate();

  /** Parse a CSV line handling quoted fields. */
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      if (char === '"') {
        if (inQuotes && line[j + 1] === '"') {
          current += '"';
          j++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    return values;
  };

  useEffect(() => {
    const fetchUsernames = async () => {
      try {
        setLoading(true);
        const res = await fetch('/data/Wrapped.WINGO tracker - Log.csv');
        if (!res.ok) return;
        const csvText = await res.text();
        const lines = csvText.split('\n').filter((l) => l.trim());
        if (lines.length < 2) return;

        const headers = parseCSVLine(lines[0]);
        const usernameIdx = headers.indexOf('Username');
        const categoryIdx = headers.indexOf('Category');
        if (usernameIdx === -1 || categoryIdx === -1) return;

        const set = new Set<string>();
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i].trim());
          if (values.length <= Math.max(usernameIdx, categoryIdx)) continue;
          const username = (values[usernameIdx] || '').trim();
          const category = (values[categoryIdx] || '').trim();
          // The challenge is mining-focused; only include mining rows.
          if (category !== 'Mining') continue;
          if (username && isPublicSafeUsername(username)) set.add(username);
        }

        setUsernames(Array.from(set).sort((a, b) => a.localeCompare(b)));
      } catch {
        // Non-fatal: leave usernames empty.
      } finally {
        setLoading(false);
      }
    };

    fetchUsernames();
  }, []);

  const handlePick = (name: string) => {
    const normalizedUsername = name.trim().toLowerCase();
    if (normalizedUsername) {
      setSelectedUsername(normalizedUsername);
      setIsOpen(false);
    }
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedUsername = selectedUsername.trim().toLowerCase();
    if (!normalizedUsername) return;

    // Session-only gate so /challenge/:username can skip the password prompt.
    sessionStorage.setItem(
      'wingo_challenge_authed',
      JSON.stringify({ username: normalizedUsername, at: Date.now() })
    );

    navigate(`/challenge/${normalizedUsername}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">The Wingo Challenge</h1>

          <form onSubmit={handleContinue}>
            <div className="mb-4">
              <label htmlFor="username-search" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <button
                  type="button"
                  id="username-search"
                  onClick={() => setIsOpen((v) => !v)}
                  onBlur={() => window.setTimeout(() => setIsOpen(false), 120)}
                  className="w-full text-left px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E6C200] focus:border-transparent bg-white"
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                >
                  {selectedUsername ? (
                    <span className="font-mono text-sm text-gray-800">{selectedUsername}</span>
                  ) : (
                    <span className="text-sm text-gray-500">Select username</span>
                  )}
                </button>

                {isOpen && (
                  <div
                    className="absolute left-0 right-0 z-10 mt-2 bg-white border border-gray-200 rounded-md shadow-md max-h-64 overflow-y-auto"
                    role="listbox"
                    aria-label="Available usernames"
                  >
                    <div className="p-2 space-y-1">
                      {loading ? (
                        <div className="text-sm text-gray-500 py-3 text-center">Loading…</div>
                      ) : usernames.length === 0 ? (
                        <div className="text-sm text-gray-500 py-3 text-center">No users found</div>
                      ) : (
                        usernames.map((u) => (
                          <button
                            key={u}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handlePick(u)}
                            className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#E6C200]"
                          >
                            <span className="font-mono text-sm text-gray-800">{u}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="challenge-password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="challenge-password"
                defaultValue=""
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E6C200] focus:border-transparent"
                placeholder="wingo"
              />
            </div>

            <button
              type="submit"
              disabled={!selectedUsername}
              className="w-full bg-[#E6C200] text-white py-2 px-4 rounded-md hover:bg-[#D4B000] focus:outline-none focus:ring-2 focus:ring-[#E6C200] focus:ring-offset-2 font-medium disabled:opacity-60 disabled:hover:bg-[#E6C200]"
            >
              View Progress
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChallengeEntry;
